import React, { useState } from 'react';

import type { Category } from '../../services/api';

import styles from './BulkActionToolbar.module.css';

/**
 * BulkActionToolbar Props
 */
type BulkActionToolbarProps = {
  readonly selectedCount: number;
  readonly totalCount: number;
  readonly categories: ReadonlyArray<Category>;
  readonly onSelectAll: () => void;
  readonly onCategoryChange: (categorySlug: string) => Promise<void>;
  readonly onDelete: () => Promise<void>;
};

/**
 * BulkActionToolbar Component
 *
 * Toolbar for bulk actions on selected images
 * Provides options to change category or delete multiple images at once
 * @param root0
 * @param root0.selectedCount
 * @param root0.totalCount
 * @param root0.categories
 * @param root0.onSelectAll
 * @param root0.onCategoryChange
 * @param root0.onDelete
 */
const BulkActionToolbar: React.FC<BulkActionToolbarProps> = ({
  selectedCount,
  totalCount,
  categories,
  onSelectAll,
  onCategoryChange,
  onDelete,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleCategoryChange = async (): Promise<void> => {
    if (!selectedCategory || selectedCount === 0) return;

    setIsProcessing(true);
    try {
      await onCategoryChange(selectedCategory);
      setSelectedCategory('');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (selectedCount === 0) return;

    setIsProcessing(true);
    try {
      await onDelete();
    } finally {
      setIsProcessing(false);
    }
  };

  const allSelected = selectedCount === totalCount && totalCount > 0;

  return (
    <div className={styles.toolbar}>
      <div className={styles.selectionInfo}>
        <button
          onClick={onSelectAll}
          className={styles.selectAllButton}
          disabled={isProcessing || totalCount === 0}
        >
          {allSelected ? 'Deselect All' : 'Select All'}
        </button>
        <span className={styles.count}>
          {selectedCount} of {totalCount} selected
        </span>
      </div>

      <div className={styles.actions}>
        {/* Category Change */}
        <div className={styles.actionGroup}>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className={styles.categorySelect}
            disabled={isProcessing || selectedCount === 0}
          >
            <option value="">Move to category...</option>
            <option value="uncategorized">Uncategorized</option>
            {['portfolio', 'shop', 'courses'].map((type) => {
              const typedCategories = categories.filter((cat) => cat.type === type);
              if (typedCategories.length === 0) return null;

              return (
                <optgroup key={type} label={type.charAt(0).toUpperCase() + type.slice(1)}>
                  {typedCategories
                    .filter((cat) => cat.enabled)
                    .sort((a, b) => a.order - b.order)
                    .map((cat) => (
                      <option key={cat._id} value={cat.slug}>
                        {cat.name}
                      </option>
                    ))}
                </optgroup>
              );
            })}
          </select>
          <button
            onClick={handleCategoryChange}
            className={styles.applyButton}
            disabled={isProcessing || !selectedCategory || selectedCount === 0}
          >
            {isProcessing ? 'Applying...' : 'Apply'}
          </button>
        </div>

        {/* Delete */}
        <button
          onClick={handleDelete}
          className={styles.deleteButton}
          disabled={isProcessing || selectedCount === 0}
        >
          {isProcessing ? 'Deleting...' : `Delete (${selectedCount})`}
        </button>
      </div>
    </div>
  );
};

export default BulkActionToolbar;
